import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { Lock, LogOut, RefreshCw, Users, Check, X, Plane, Search, Mail, Images, Trash2, MessageSquare, Pencil, Plus, Minus, Save, Gift as GiftIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rsvp_admin_token";

export default function AdminPanel() {
  const [token, setToken] = useState(
    () =>
      new URLSearchParams(window.location.search).get("token") ||
      localStorage.getItem(TOKEN_KEY) ||
      ""
  );
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | accept | decline
  const [sendingSummary, setSendingSummary] = useState(false);
  const [tab, setTab] = useState("rsvp"); // rsvp | guestbook | gifts
  const [posts, setPosts] = useState([]);
  const [giftNotes, setGiftNotes] = useState([]);
  const [editing, setEditing] = useState(null); // rsvp being edited
  const [savingEdit, setSavingEdit] = useState(false);

  const sendSummary = async () => {
    setSendingSummary(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/daily-summary`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Günlük özet gönderildi (${data.count} yanıt).`);
    } catch (e) {
      if (e.response?.status === 401) logout();
      else toast.error("Özet gönderilemedi.");
    } finally {
      setSendingSummary(false);
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setRows([]);
    setStats(null);
    setPosts([]);
    setGiftNotes([]);
  }, []);

  const load = useCallback(
    async (tk) => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${tk}` };
        const [r, s, g] = await Promise.all([
          axios.get(`${API}/rsvp`, { headers }),
          axios.get(`${API}/rsvp/stats`, { headers }),
          axios.get(`${API}/guestbook`, { headers }),
        ]);
        setRows(r.data);
        setStats(s.data);
        setPosts(g.data);
        try {
          const gn = await axios.get(`${API}/gift-note`, { headers });
          setGiftNotes(gn.data);
        } catch (_) {
          setGiftNotes([]);
        }
      } catch (e) {
        if (e.response?.status === 401) {
          toast.error("Oturum süresi doldu, tekrar giriş yapın.");
          logout();
        } else {
          toast.error("Veriler alınamadı.");
        }
      } finally {
        setLoading(false);
      }
    },
    [logout]
  );

  const deletePost = async (id) => {
    if (!window.confirm("Bu paylaşımı kaldırmak istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/guestbook/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Paylaşım kaldırıldı.");
    } catch (e) {
      if (e.response?.status === 401) logout();
      else toast.error("Kaldırılamadı.");
    }
  };

  const deleteRsvp = async (id) => {
    if (!window.confirm("Bu katılım kaydını silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/rsvp/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Katılım kaydı silindi.");
      load(token);
    } catch (e) {
      if (e.response?.status === 401) logout();
      else toast.error("Silinemedi.");
    }
  };

  const deleteGiftNote = async (id) => {
    if (!window.confirm("Bu tebrik notunu kaldırmak istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/gift-note/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGiftNotes((prev) => prev.filter((n) => n.id !== id));
      toast.success("Tebrik notu kaldırıldı.");
    } catch (e) {
      if (e.response?.status === 401) logout();
      else toast.error("Kaldırılamadı.");
    }
  };

  const saveEdit = async () => {
    if (!editing?.name?.trim()) {
      toast.error("İsim boş olamaz.");
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(
        `${API}/rsvp/${editing.id}`,
        {
          name: editing.name.trim(),
          attending: editing.attending,
          guest_count: editing.attending === "accept" ? Number(editing.guest_count) || 1 : 0,
          contact: editing.contact || "",
          message: editing.message || "",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Katılım kaydı güncellendi.");
      setEditing(null);
      load(token);
    } catch (e) {
      if (e.response?.status === 401) logout();
      else toast.error("Güncellenemedi.");
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Hoş geldiniz!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Giriş başarısız");
    } finally {
      setLoggingIn(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen leather flex items-center justify-center px-5">
        <Toaster position="top-center" richColors />
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm paper rounded-2xl p-8 shadow-2xl text-center"
        >
          <div className="w-14 h-14 rounded-full leather flex items-center justify-center mx-auto mb-5">
            <Lock className="text-[var(--gold)]" size={22} />
          </div>
          <h1 className="font-serif text-3xl text-[var(--forest)]">Katılım Paneli</h1>
          <p className="font-sans text-sm text-[var(--olive)]/60 mt-1 mb-6">Devam etmek için şifre girin</p>
          <input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Şifre"
            className="w-full bg-transparent border-b border-[var(--gold-dark)]/40 py-2 font-serif text-lg text-center text-[var(--forest)] outline-none focus:border-[var(--gold-dark)] transition-colors mb-6"
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loggingIn}
            className="w-full py-3 rounded-full font-sans tracking-[0.18em] text-xs uppercase text-[var(--olive)] bg-gradient-to-r from-[#BF953F] via-[#FCF6BA] to-[#B38728] transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60"
          >
            {loggingIn ? "Giriş yapılıyor..." : "Giriş Yap"}
          </button>
        </form>
      </div>
    );
  }

  const filtered = rows.filter((r) => {
    const okStatus = filter === "all" || r.attending === filter;
    const okName = r.name.toLowerCase().includes(query.trim().toLowerCase());
    return okStatus && okName;
  });

  return (
    <div className="min-h-screen leather px-4 sm:px-8 py-8">
      <Toaster position="top-center" richColors />
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <Plane className="text-[var(--gold)] rotate-45" size={18} />
            <h1 className="gold-text font-serif text-2xl sm:text-3xl">Katılım Paneli</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh"
              onClick={() => load(token)}
              className="w-10 h-10 rounded-full border border-[var(--gold)]/40 flex items-center justify-center text-[var(--gold)] hover:bg-[var(--olive)]/50 transition-colors"
              title="Yenile"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="flex items-center gap-2 px-4 h-10 rounded-full border border-[var(--gold)]/40 text-[var(--gold)] text-xs uppercase tracking-widest hover:bg-[var(--olive)]/50 transition-colors"
            >
              <LogOut size={14} /> Çıkış
            </button>
          </div>
        </div>

        {/* tab switcher */}
        <div className="flex items-center gap-2 mb-8" data-testid="admin-tabs">
          <button
            data-testid="tab-rsvp"
            onClick={() => setTab("rsvp")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-sans text-xs uppercase tracking-widest transition-colors ${
              tab === "rsvp"
                ? "bg-gradient-to-r from-[#BF953F] to-[#B38728] text-[var(--olive)]"
                : "border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--olive)]/50"
            }`}
          >
            <Users size={14} /> Katılımlar
          </button>
          <button
            data-testid="tab-guestbook"
            onClick={() => setTab("guestbook")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-sans text-xs uppercase tracking-widest transition-colors ${
              tab === "guestbook"
                ? "bg-gradient-to-r from-[#BF953F] to-[#B38728] text-[var(--olive)]"
                : "border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--olive)]/50"
            }`}
          >
            <Images size={14} /> Anılar
            {posts.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[var(--olive)]/60 text-[var(--gold-light)] text-[10px]">
                {posts.length}
              </span>
            )}
          </button>
          <button
            data-testid="tab-gifts"
            onClick={() => setTab("gifts")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-sans text-xs uppercase tracking-widest transition-colors ${
              tab === "gifts"
                ? "bg-gradient-to-r from-[#BF953F] to-[#B38728] text-[var(--olive)]"
                : "border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--olive)]/50"
            }`}
          >
            <GiftIcon size={14} /> Tebrikler
            {giftNotes.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[var(--olive)]/60 text-[var(--gold-light)] text-[10px]">
                {giftNotes.length}
              </span>
            )}
          </button>
        </div>

        {tab === "rsvp" && (
        <>
        {/* stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-5 mb-8" data-testid="admin-stats">
            <StatCard icon={<Users size={18} />} label="Toplam Yanıt" value={stats.total} />
            <StatCard icon={<Check size={18} />} label="Katılacak" value={stats.accepts} tone="ok" />
            <StatCard icon={<X size={18} />} label="Katılamayacak" value={stats.declines} tone="no" />
            <StatCard icon={<Users size={18} />} label="Beklenen Kişi" value={stats.expected_guests} tone="gold" />
          </div>
        )}

        {/* toolbar: search + filter + summary */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5" data-testid="admin-toolbar">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gold)]/60" />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="İsme göre ara..."
              className="w-full pl-9 pr-4 py-2.5 rounded-full bg-[var(--olive)]/40 border border-[var(--gold)]/25 text-[var(--cream)] text-sm outline-none focus:border-[var(--gold)]/60 transition-colors placeholder:text-[var(--gold-light)]/30"
            />
          </div>
          <div className="flex items-center rounded-full border border-[var(--gold)]/25 overflow-hidden bg-[var(--olive)]/40 text-xs font-sans">
            {[
              { k: "all", label: "Tümü" },
              { k: "accept", label: "Katılacak" },
              { k: "decline", label: "Katılamayacak" },
            ].map((f) => (
              <button
                key={f.k}
                data-testid={`filter-${f.k}`}
                onClick={() => setFilter(f.k)}
                className={`px-4 py-2.5 tracking-wide transition-colors ${
                  filter === f.k
                    ? "bg-gradient-to-r from-[#BF953F] to-[#B38728] text-[var(--olive)]"
                    : "text-[var(--gold)] hover:bg-[var(--olive)]/60"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button
            data-testid="send-summary-btn"
            onClick={sendSummary}
            disabled={sendingSummary}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-[var(--gold)]/40 text-[var(--gold)] text-xs uppercase tracking-widest hover:bg-[var(--olive)]/60 transition-colors disabled:opacity-60"
          >
            <Mail size={14} /> {sendingSummary ? "Gönderiliyor..." : "Özet Gönder"}
          </button>
        </div>

        {/* table */}
        <div className="paper rounded-2xl overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="admin-table">
              <thead>
                <tr className="leather text-[var(--gold)] font-mono text-[10px] tracking-[0.15em] uppercase">
                  <th className="px-4 py-3">Ad Soyad</th>
                  <th className="px-4 py-3">Durum</th>
                  <th className="px-4 py-3">Kişi</th>
                  <th className="px-4 py-3">İletişim</th>
                  <th className="px-4 py-3">Mesaj</th>
                  <th className="px-4 py-3">Tarih</th>
                  <th className="px-4 py-3 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody className="text-[var(--olive)]">
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center font-serif text-lg text-[var(--olive)]/50">
                      {loading ? "Yükleniyor..." : rows.length === 0 ? "Henüz katılım bildirimi yok." : "Sonuç bulunamadı."}
                    </td>
                  </tr>
                )}
                {filtered.map((r) => {
                  const going = r.attending === "accept";
                  return (
                    <tr key={r.id} className="border-b border-[var(--gold-dark)]/15 hover:bg-[var(--ivory)]/60 transition-colors">
                      <td className="px-4 py-3 font-serif text-lg text-[var(--forest)]">{r.name}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-sans ${going ? "bg-[#1f7a4d]/15 text-[#1f7a4d]" : "bg-[#a33]/15 text-[#a33]"}`}>
                          {going ? <Check size={12} /> : <X size={12} />}
                          {going ? "Katılacak" : "Katılamayacak"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono">{going ? r.guest_count : "-"}</td>
                      <td className="px-4 py-3 text-sm">{r.contact || "-"}</td>
                      <td className="px-4 py-3 text-sm max-w-[220px] truncate" title={r.message}>{r.message || "-"}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">{fmt(r.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            data-testid={`rsvp-edit-${r.id}`}
                            onClick={() => setEditing({ ...r, guest_count: r.guest_count || 1 })}
                            className="w-8 h-8 rounded-full border border-[var(--gold-dark)]/40 text-[var(--gold-dark)] flex items-center justify-center hover:bg-[var(--forest)] hover:text-[var(--cream)] transition-colors"
                            title="Düzenle"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            data-testid={`rsvp-delete-${r.id}`}
                            onClick={() => deleteRsvp(r.id)}
                            className="w-8 h-8 rounded-full border border-[#a33]/40 text-[#a33] flex items-center justify-center hover:bg-[#a33]/10 transition-colors"
                            title="Sil"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}

        {tab === "guestbook" && (
          <div data-testid="guestbook-admin">
            {posts.length === 0 ? (
              <div className="paper rounded-2xl p-12 text-center font-serif text-lg text-[var(--olive)]/50">
                {loading ? "Yükleniyor..." : "Henüz anı paylaşımı yok."}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {posts.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`guest-post-${p.id}`}
                    className="paper rounded-2xl p-5 shadow-xl flex flex-col"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-serif text-xl text-[var(--forest)]">{p.name}</p>
                        <p className="font-mono text-[10px] tracking-wide text-[var(--olive)]/50 mt-0.5">
                          {fmt(p.created_at)}
                        </p>
                      </div>
                      <button
                        data-testid={`guest-post-delete-${p.id}`}
                        onClick={() => deletePost(p.id)}
                        className="w-8 h-8 rounded-full border border-[#a33]/40 text-[#a33] flex items-center justify-center hover:bg-[#a33]/10 transition-colors shrink-0"
                        title="Kaldır"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    {p.message && (
                      <div className="flex gap-2 mt-3 text-[var(--olive)]">
                        <MessageSquare size={15} className="text-[var(--gold-dark)] shrink-0 mt-0.5" />
                        <p className="font-serif text-[15px] leading-snug">{p.message}</p>
                      </div>
                    )}

                    {p.photos?.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mt-4">
                        {p.photos.map((ph, i) => (
                          <a
                            key={i}
                            href={`${API}/files/${ph.storage_path}?auth=${token}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`guest-photo-${p.id}-${i}`}
                            className="block aspect-square rounded-lg overflow-hidden border border-[var(--gold-dark)]/30 hover:opacity-90 transition-opacity"
                          >
                            <img
                              src={`${API}/files/${ph.storage_path}?auth=${token}`}
                              alt={`${p.name} ${i + 1}`}
                              loading="lazy"
                              className="w-full h-full object-cover"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "gifts" && (
          <div data-testid="gifts-admin">
            {giftNotes.length === 0 ? (
              <div className="paper rounded-2xl p-12 text-center font-serif text-lg text-[var(--olive)]/50">
                {loading ? "Yükleniyor..." : "Henüz tebrik notu yok."}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {giftNotes.map((n) => (
                  <div
                    key={n.id}
                    data-testid={`gift-note-${n.id}`}
                    className="paper rounded-2xl p-5 shadow-xl flex flex-col"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-serif text-xl text-[var(--forest)]">{n.name}</p>
                        <p className="font-mono text-[10px] tracking-wide text-[var(--olive)]/50 mt-0.5">
                          {fmt(n.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {n.amount && (
                          <span className="px-2.5 py-1 rounded-full bg-[var(--gold-dark)]/15 text-[var(--gold-dark)] font-mono text-xs">
                            {n.amount}
                          </span>
                        )}
                        <button
                          data-testid={`gift-note-delete-${n.id}`}
                          onClick={() => deleteGiftNote(n.id)}
                          className="w-8 h-8 rounded-full border border-[#a33]/40 text-[#a33] flex items-center justify-center hover:bg-[#a33]/10 transition-colors"
                          title="Kaldır"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    {n.message && (
                      <div className="flex gap-2 mt-3 text-[var(--olive)]">
                        <MessageSquare size={15} className="text-[var(--gold-dark)] shrink-0 mt-0.5" />
                        <p className="font-serif text-[15px] leading-snug">{n.message}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* edit modal */}
        {editing && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
            data-testid="rsvp-edit-modal"
            onClick={() => !savingEdit && setEditing(null)}
          >
            <div
              className="w-full max-w-md paper rounded-2xl p-6 sm:p-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-serif text-2xl text-[var(--forest)]">Katılımı Düzenle</h2>
                <button
                  data-testid="rsvp-edit-close"
                  onClick={() => setEditing(null)}
                  className="w-9 h-9 rounded-full border border-[var(--gold-dark)]/40 text-[var(--olive)] flex items-center justify-center hover:bg-[var(--ivory)] transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="space-y-5 text-left">
                <div>
                  <label className="font-sans text-[11px] tracking-[0.2em] uppercase text-[var(--olive)]/60 block mb-2">Ad Soyad</label>
                  <input
                    data-testid="rsvp-edit-name"
                    value={editing.name}
                    onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))}
                    className="w-full bg-transparent border-b border-[var(--gold-dark)]/40 py-2 font-serif text-lg text-[var(--forest)] outline-none focus:border-[var(--gold-dark)] transition-colors"
                  />
                </div>

                <div>
                  <label className="font-sans text-[11px] tracking-[0.2em] uppercase text-[var(--olive)]/60 block mb-2">Katılım Durumu</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      data-testid="rsvp-edit-accept"
                      onClick={() => setEditing((p) => ({ ...p, attending: "accept" }))}
                      className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-sans transition-all ${editing.attending === "accept" ? "bg-[var(--forest)] text-[var(--cream)] border-[var(--forest)]" : "border-[var(--gold-dark)]/40 text-[var(--olive)]/70 hover:border-[var(--gold-dark)]"}`}
                    >
                      <Check size={15} /> Katılacak
                    </button>
                    <button
                      type="button"
                      data-testid="rsvp-edit-decline"
                      onClick={() => setEditing((p) => ({ ...p, attending: "decline" }))}
                      className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-sans transition-all ${editing.attending === "decline" ? "bg-[#a33] text-white border-[#a33]" : "border-[var(--gold-dark)]/40 text-[var(--olive)]/70 hover:border-[var(--gold-dark)]"}`}
                    >
                      <X size={15} /> Katılamayacak
                    </button>
                  </div>
                </div>

                {editing.attending === "accept" && (
                  <div>
                    <label className="font-sans text-[11px] tracking-[0.2em] uppercase text-[var(--olive)]/60 block mb-2">Kişi Sayısı</label>
                    <div className="flex items-center gap-4">
                      <button type="button" data-testid="rsvp-edit-minus" onClick={() => setEditing((p) => ({ ...p, guest_count: Math.max(1, (Number(p.guest_count) || 1) - 1) }))}
                        className="w-10 h-10 rounded-full border border-[var(--gold-dark)]/40 flex items-center justify-center text-[var(--forest)] hover:bg-[var(--forest)] hover:text-[var(--cream)] transition-colors">
                        <Minus size={16} />
                      </button>
                      <span data-testid="rsvp-edit-count" className="font-serif text-2xl text-[var(--forest)] w-8 text-center tabular-nums">{editing.guest_count}</span>
                      <button type="button" data-testid="rsvp-edit-plus" onClick={() => setEditing((p) => ({ ...p, guest_count: Math.min(20, (Number(p.guest_count) || 1) + 1) }))}
                        className="w-10 h-10 rounded-full border border-[var(--gold-dark)]/40 flex items-center justify-center text-[var(--forest)] hover:bg-[var(--forest)] hover:text-[var(--cream)] transition-colors">
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="font-sans text-[11px] tracking-[0.2em] uppercase text-[var(--olive)]/60 block mb-2">İletişim</label>
                  <input
                    data-testid="rsvp-edit-contact"
                    value={editing.contact || ""}
                    onChange={(e) => setEditing((p) => ({ ...p, contact: e.target.value }))}
                    className="w-full bg-transparent border-b border-[var(--gold-dark)]/40 py-2 font-serif text-lg text-[var(--forest)] outline-none focus:border-[var(--gold-dark)] transition-colors"
                  />
                </div>

                <div>
                  <label className="font-sans text-[11px] tracking-[0.2em] uppercase text-[var(--olive)]/60 block mb-2">Mesaj</label>
                  <textarea
                    data-testid="rsvp-edit-message"
                    rows={2}
                    value={editing.message || ""}
                    onChange={(e) => setEditing((p) => ({ ...p, message: e.target.value }))}
                    className="w-full bg-transparent border-b border-[var(--gold-dark)]/40 py-2 font-serif text-lg text-[var(--forest)] outline-none focus:border-[var(--gold-dark)] transition-colors resize-none"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 mt-7">
                <button
                  data-testid="rsvp-edit-cancel"
                  onClick={() => setEditing(null)}
                  className="flex-1 py-3 rounded-full border border-[var(--gold-dark)]/40 text-[var(--olive)] font-sans tracking-[0.15em] text-xs uppercase hover:bg-[var(--ivory)] transition-colors"
                >
                  Vazgeç
                </button>
                <button
                  data-testid="rsvp-edit-save"
                  onClick={saveEdit}
                  disabled={savingEdit}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full font-sans tracking-[0.15em] text-xs uppercase text-[var(--olive)] bg-gradient-to-r from-[#BF953F] via-[#FCF6BA] to-[#B38728] transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60"
                >
                  <Save size={14} /> {savingEdit ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ icon, label, value, tone }) => {
  const color =
    tone === "ok" ? "text-[#3fae74]" : tone === "no" ? "text-[#d9736b]" : "text-[var(--gold)]";
  return (
    <div className="rounded-xl border border-[var(--gold)]/20 bg-[var(--olive)]/40 p-4 sm:p-5">
      <div className={`flex items-center gap-2 ${color}`}>{icon}
        <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-[var(--gold)]/70">{label}</span>
      </div>
      <p className={`font-serif font-bold text-3xl sm:text-4xl mt-2 ${color}`}>{value}</p>
    </div>
  );
};

const fmt = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};
